import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap, Polygon } from 'react-leaflet';
import { Button, Input, message, DatePicker, Form, ConfigProvider, Tooltip as AntdTooltip, Modal, Upload, Select, Checkbox, Row, Col, Divider } from 'antd';
import { Box, Slider } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import api from "../../authentication/axiosInstance";
import styled from 'styled-components';
import RedMarker from '../../assets/RedMarker.png';
import BlueMarker from '../../assets/BlueMarker.png';
import MarkerShadow from '../../assets/MarkerShadow.png';
import { StandaloneSearchBox, } from "@react-google-maps/api";
import { IconBuildingFactory2, IconX, IconUpload, IconPlus, IconTrash, IconEdit, IconMap, IconRuler, IconCalendar, IconUser, IconSnowflake, IconSalt, IconRoad } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import dayjs from 'dayjs';
import styles from './EstimatePage.module.css';

const { Option } = Select;

const customBlueMarkerIcon = L.icon({
  iconUrl: BlueMarker,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: MarkerShadow,
  shadowSize: [41, 41],
});

const customRedMarkerIcon = L.icon({
  iconUrl: RedMarker,
  iconSize: [25, 40],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: MarkerShadow,
  shadowSize: [41, 41],
});

const LeftPanel = styled.div`
  width: 300px;
  min-width: 300px;
  padding: 10px;
  overflow-y: auto;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const FormSection = styled.div`
  flex: 1;
  padding: 10px;
  background-color: #ffffff;
  border-bottom: 1px solid #d9d9d9;
`;

const CalculationSection = styled.div`
  flex: 1;
  padding: 10px;
`;

const RightPanel = styled.div`
  position: relative;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
`;

function SetViewOnClick({ coords }) {
  const map = useMap();

  useEffect(() => {
    if (coords) {
      map.setView(coords, 13);
    }
  }, [coords, map]);
  return null;
}

const EstimatePage = () => {
  const [sites, setSites] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [isCreatingEstimate, setIsCreatingEstimate] = useState(false);
  const [isEditingEstimate, setIsEditingEstimate] = useState(false);
  const [form] = Form.useForm();
  const [mapPosition, setMapPosition] = useState(null);
  const [newEstimatePosition, setNewEstimatePosition] = useState(null);
  const [estimateLatLong, setEstimateLatLong] = useState([0, 0]);
  const [estimateDate, setEstimateDate] = useState(null);
  const [addressInput, setAddressInput] = useState('');
  const [estimateAddress, setEstimateAddress] = useState('');
  const [saltPrice, setSaltPrice] = useState(0);
  const [serviceLevel, setServiceLevel] = useState(1);
  const [estimateParameters, setEstimateParameters] = useState(null);
  const [validAddress, setValidAddress] = useState(false);
  const [boundaryPolygon, setBoundaryPolygon] = useState(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kmzLayers, setKmzLayers] = useState([]);
  const [selectedParkingLayers, setSelectedParkingLayers] = useState([]);
  const [selectedWalkwayLayers, setSelectedWalkwayLayers] = useState([]);
  const [showLayerSelector, setShowLayerSelector] = useState(false);
  const [parkingLotSqft, setParkingLotSqft] = useState('');
  const [walkwaySqft, setWalkwaySqft] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [newClient, setNewClient] = useState({
    client_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_phone: '',
    contact_email: ''
  });

  const searchBoxRef = useRef(null);

  const fetchEstimates = async () => {
    try {
      const response = await api.get('/estimate/estimates/');
      setEstimates(response.data);
    } catch (error) {
      console.error('Failed to fetch estimates:', error);
      message.error('Failed to fetch estimates');
    }
  }

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites/');
      setSites(response.data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      message.error('Failed to fetch sites');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/estimate/estimates/get_clients/');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  useEffect(() => {
    fetchEstimates();
    fetchSites();
    fetchClients();
  }, []);

  const handleDateChange = (date) => {
    setEstimateDate(date); // store the dayjs object in state
  };

  const handleCreateEstimate = () => {
    setIsCreatingEstimate(true);
    setAddressInput("");
    setSelectedEstimate(null);
    form.resetFields();
  };

  const handleKmzUpload = async options => {
    const { file, onSuccess, onError } = options;
    
    // Check if file is a KMZ file
    if (!file.name.endsWith('.kmz')) {
      message.error('Please upload a KMZ file');
      onError('Not a KMZ file');
      return;
    }

    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/estimate/estimates/upload_kmz/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      onSuccess('OK');
      message.success('File uploaded successfully');
      
      // Extract data from the response
      const { geojson, centroid, lots_sqft, walkway_sqft, address, client } = response.data;
      
      // Set the polygon data
      setBoundaryPolygon(geojson);
      
      // Extract layers for selection
      const layers = geojson.features.map((feature, index) => ({
        id: index,
        name: feature.properties.name || `Polygon ${index + 1}`,
        is_walkway: feature.properties.is_walkway,
        area_sqft: feature.properties.area_sqft || 0,
        selected: false
      }));
      
      // Pre-select layers based on is_walkway property
      const parkingLayers = layers
        .filter(layer => !layer.is_walkway)
        .map(layer => layer.id);
      
      const walkwayLayers = layers
        .filter(layer => layer.is_walkway)
        .map(layer => layer.id);
      
      setKmzLayers(layers);
      setSelectedParkingLayers(parkingLayers);
      setSelectedWalkwayLayers(walkwayLayers);
      
      // Set the extracted data in the form
      form.setFieldsValue({ 
        lots_sqft,
        walkway_sqft: walkway_sqft || '', // Use empty string if walkway_sqft is 0 or undefined
        client: client || ''
      });
      
      // Also update the state variables
      setParkingLotSqft(lots_sqft);
      setWalkwaySqft(walkway_sqft || '');
      
      // Set today's date if no date is set
      if (!estimateDate) {
        const today = dayjs();
        setEstimateDate(today);
        form.setFieldsValue({ date: today });
      }
      
      // If address was found in the KMZ, set it
      if (address && address.trim() !== '') {
        setAddressInput(address);
        setEstimateAddress(address);
        setValidAddress(true);
      } else if (centroid && centroid.length === 2) {
        // No address in KMZ, get it from Google Maps API based on coordinates
        const [lng, lat] = centroid;
        setMapPosition([lat, lng]);
        setEstimateLatLong({lat, lng});
        
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
          // Use a promise to ensure we get the result before proceeding
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const formattedAddress = results[0].formatted_address;
              console.log("Geocoded address:", formattedAddress);
              setAddressInput(formattedAddress);
              setEstimateAddress(formattedAddress);
              setValidAddress(true);
              form.setFieldsValue({ address: formattedAddress });
            } else {
              message.warning('Could not determine address from location. Please enter manually.');
            }
          });
        }
      } else {
        message.warning('No address or location found in KMZ file. Please enter address manually.');
      }
      
      // Close the upload modal
      setUploadModalVisible(false);
      
      // Show layer selector if we have layers
      if (layers.length > 0) {
        setShowLayerSelector(true);
      }
    } catch (error) {
      console.error('Error uploading KMZ file:', error);
      message.error('Failed to upload KMZ file');
      onError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateEstimate = () => {
    const values = form.getFieldsValue();
    
    // Make sure we have a valid address
    if (!validAddress || !estimateAddress) {
      message.error('Please enter a valid address');
      return;
    }
    
    // Make sure we have values for required fields
    if (!values.lots_sqft) {
      message.error('Please enter the parking lot square footage');
      return;
    }
    
    // Make sure we have a valid date
    if (!estimateDate) {
      const today = dayjs();
      setEstimateDate(today);
      values.date = today;
    }
    
    // Make sure we have latitude and longitude
    if (!estimateLatLong || estimateLatLong.lat === 0 || estimateLatLong.lng === 0) {
      message.error('Invalid location. Please ensure address is geocoded correctly.');
      return;
    }
    
    // If we have a boundary polygon, update it to include the layer selections
    let updatedBoundaryPolygon = null;
    if (boundaryPolygon) {
      updatedBoundaryPolygon = {
        ...boundaryPolygon,
        features: boundaryPolygon.features.map((feature, index) => {
          const isParkingLayer = selectedParkingLayers.includes(index);
          const isWalkwayLayer = selectedWalkwayLayers.includes(index);
          
          // Update the feature properties to match the current selection
          return {
            ...feature,
            properties: {
              ...feature.properties,
              is_walkway: isWalkwayLayer,
              is_parking: isParkingLayer,
              is_selected: isParkingLayer || isWalkwayLayer
            }
          };
        })
      };
    }
    
    // Prepare parameters
    setEstimateParameters({
      address: estimateAddress,
      client: values.client || '',
      date: estimateDate.format('YYYY-MM-DD'),
      latitude: estimateLatLong.lat,
      longitude: estimateLatLong.lng,
      lots_sqft: values.lots_sqft, 
      walkway_sqft: values.walkway_sqft || 0, 
      service_level: values.service_level || serviceLevel, 
      salt_price: values.salt_price || saltPrice,
      boundary_polygon: updatedBoundaryPolygon || boundaryPolygon,
      client_id: selectedClient,
    });
  };

  useEffect(() => {
    if (estimateParameters) {
      const generateEstimate = async () => {
        if (!validAddress) {
          message.error('Invalid address. Please enter a valid address.');
          return;
        }
        try {
          await api.post('/estimate/estimates/generate_estimate/', estimateParameters)
          .then((response) => {
            message.success(response.data.message + ".")
          })
          setIsCreatingEstimate(false);
          form.resetFields();
          fetchEstimates();
          setSelectedEstimate(null);
        } catch (error) {
          message.error('Failed to generate estimate')
        }
      };
      
      generateEstimate();
    }
  }, [estimateParameters]);
  

  const handleDeleteEstimate = async (estimate) => {
    try {
      await api.delete(`/estimate/estimates/${estimate.id}/`);
      fetchEstimates();
      if (estimates && estimates.length > 0) {
        setMapPosition([estimates[0].latitude, estimates[0].longitude]);
      }
      message.success('Estimate deleted successfully');
      setSelectedEstimate(null);
    } catch (error) {
      console.error('Failed to delete estimate:', error);
      message.error('Failed to delete estimate');
    }
  };

  const handleEditEstimate = (estimate) => {
    setIsEditingEstimate(true);
    setSelectedEstimate(estimate);
    setIsCreatingEstimate(false);
    
    // Set up the layers and selections (similar to loadEstimateWithLayers)
    if (estimate.boundary_polygon && estimate.boundary_polygon.features) {
      // Create layer data from features
      const layers = estimate.boundary_polygon.features.map((feature, index) => ({
        id: index,
        name: feature.properties.name || `Polygon ${index + 1}`,
        is_walkway: feature.properties.is_walkway || false,
        area_sqft: feature.properties.area_sqft || 0,
        selected: feature.properties.is_selected !== false
      }));
      
      // Set up selected layers based on properties
      const parkingLayers = [];
      const walkwayLayers = [];
      
      estimate.boundary_polygon.features.forEach((feature, index) => {
        if (feature.properties.is_selected !== false) {
          if (feature.properties.is_walkway) {
            walkwayLayers.push(index);
          } else {
            parkingLayers.push(index);
          }
        }
      });
      
      // Set the layers state
      setKmzLayers(layers);
      setSelectedParkingLayers(parkingLayers);
      setSelectedWalkwayLayers(walkwayLayers);
    }
    
    // Set form values
    form.setFieldsValue({
      address: estimate.address,
      client: estimate.client,
      date: estimate.date ? dayjs(estimate.date) : null,
      lots_sqft: estimate.lots_sqft,
      walkway_sqft: estimate.walkway_sqft,
      service_level: estimate.service_level,
      salt_price: estimate.salt_price
    });
    
    // Set other state values
    setParkingLotSqft(estimate.lots_sqft);
    setWalkwaySqft(estimate.walkway_sqft);
    setServiceLevel(estimate.service_level);
    setSaltPrice(estimate.salt_price);
    setMapPosition([estimate.latitude, estimate.longitude]);
  };

  const handleUpdateEstimate = async () => {
    const values = form.getFieldsValue();
    const updatedEstimate = {
      ...selectedEstimate,
      ...values,
    };

    try {
      await api.put(`/estimate/estimates/${selectedEstimate.id}/`, updatedEstimate);
      message.success('Estimate updated successfully');
      fetchEstimates();
      setIsEditingEstimate(false);
      setSelectedEstimate(null);
    } catch (error) {
      console.error('Failed to update estimate:', error);
      message.error('Failed to update estimate');
    }
  };

  const serviceLevels = [
    {
      value: 1,
      label: '1',
    },
    {
      value: 2,
      label: '2',
    },
    {
      value: 3,
      label: '3',
    },
    {
      value: 4,
      label: '4',
    },
    {
      value: 5,
      label: '5',
    },
  ];
  const saltPriceLevels = [
    {
      value: 0,
      label: '0',
    },
    {
      value: 25,
      label: '25',
    },
    {
      value: 50,
      label: '50',
    },
    {
      value: 75,
      label: '75',
    },
    {
      value: 100,
      label: '100',
    },
    {
      value: 125,
      label: '125',
    },
    {
      value: 150,
      label: '150',
    },
    {
      value: 175,
      label: '175',
    },
    {
      value: 200,
      label: '200',
    }
  ];

  
  useEffect(() => {
    if (selectedEstimate) {
      setMapPosition([selectedEstimate.latitude, selectedEstimate.longitude])
    }
  },[selectedEstimate])

  const onSearchBoxLoaded = (ref) => {
    searchBoxRef.current = ref;
  };

  const onPlacesChanged = () => {
    const places = searchBoxRef.current.getPlaces();

    if (places && places.length > 0) {
      const place = places[0];
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setValidAddress(true);
      setEstimateLatLong({lat: lat, lng: lng});
      setAddressInput(place.formatted_address);
      setEstimateAddress(place.formatted_address);
      setNewEstimatePosition({ lat, lng });
      setMapPosition([lat, lng]);
    }
  };

  useEffect(() => { //set valid address to false when the input is cleared
    if (addressInput === "") {
      setValidAddress(false);
      setEstimateAddress("");
      setNewEstimatePosition(null);
      setEstimateLatLong([0, 0]);
      setEstimateParameters(null);
    }

  }, [addressInput])

  // Add function to handle selection changes and recalculate areas
  const handleLayerSelectionChange = () => {
    if (!boundaryPolygon || !boundaryPolygon.features) return;
    
    // Calculate lots_sqft (parking lot area)
    let lots_sqft = 0;
    selectedParkingLayers.forEach(id => {
      const feature = boundaryPolygon.features[id];
      if (feature && feature.properties && feature.properties.area_sqft) {
        lots_sqft += feature.properties.area_sqft;
      }
    });
    
    // Calculate walkway_sqft
    let walkway_sqft = 0;
    selectedWalkwayLayers.forEach(id => {
      const feature = boundaryPolygon.features[id];
      if (feature && feature.properties && feature.properties.area_sqft) {
        walkway_sqft += feature.properties.area_sqft;
      }
    });
    
    const roundedParkingArea = Math.round(lots_sqft);
    const roundedWalkwayArea = Math.round(walkway_sqft);
    
    console.log("Updating form with calculated areas:", { 
      lots_sqft: roundedParkingArea, 
      walkway_sqft: roundedWalkwayArea
    });
    
    // Update the state variables directly
    setParkingLotSqft(roundedParkingArea);
    setWalkwaySqft(roundedWalkwayArea);
    
    // Update form values
    form.setFieldsValue({
      lots_sqft: roundedParkingArea,
      walkway_sqft: roundedWalkwayArea
    });
  };

  // Effect to recalculate areas when selections change
  useEffect(() => {
    handleLayerSelectionChange();
  }, [selectedParkingLayers, selectedWalkwayLayers]);

  // Update the function that loads an estimate to handle selected layers properly
  const loadEstimateWithLayers = (estimate) => {
    setAddressInput("");
    setSelectedEstimate(estimate);
    setNewEstimatePosition(null);
    setIsCreatingEstimate(false);
    setIsEditingEstimate(false);
    
    // Set the polygon data
    setBoundaryPolygon(estimate.boundary_polygon);
    
    // If boundary polygon exists, set up the layers and selections
    if (estimate.boundary_polygon && estimate.boundary_polygon.features) {
      // Create layer data from features
      const layers = estimate.boundary_polygon.features.map((feature, index) => ({
        id: index,
        name: feature.properties.name || `Polygon ${index + 1}`,
        is_walkway: feature.properties.is_walkway || false,
        area_sqft: feature.properties.area_sqft || 0,
        selected: feature.properties.is_selected !== false // Default to true if property doesn't exist
      }));
      
      // Set up selected layers based on properties
      const parkingLayers = [];
      const walkwayLayers = [];
      
      estimate.boundary_polygon.features.forEach((feature, index) => {
        if (feature.properties.is_selected !== false) { // Ensure only selected layers are included
          if (feature.properties.is_walkway) {
            walkwayLayers.push(index);
          } else {
            parkingLayers.push(index);
          }
        }
      });
      
      // Set the layers state
      setKmzLayers(layers);
      setSelectedParkingLayers(parkingLayers);
      setSelectedWalkwayLayers(walkwayLayers);
    }
    
    // Set form values
    form.setFieldsValue({
      address: estimate.address,
      client: estimate.client,
      date: estimate.date ? dayjs(estimate.date) : null,
      lots_sqft: estimate.lots_sqft,
      walkway_sqft: estimate.walkway_sqft,
      service_level: estimate.service_level,
      salt_price: estimate.salt_price
    });
    
    // Set other state values
    setParkingLotSqft(estimate.lots_sqft);
    setWalkwaySqft(estimate.walkway_sqft);
    setServiceLevel(estimate.service_level);
    setSaltPrice(estimate.salt_price);
    setSelectedClient(estimate.client_id);
  };

  const handleCreateClient = async () => {
    if (!newClient.client_name.trim()) {
      message.error('Client name cannot be empty');
      return;
    }

    try {
      const response = await api.post('/estimate/estimates/create_client/', newClient);
      
      // Add the new client to the list
      setClients([...clients, response.data]);
      
      // Select the new client
      setSelectedClient(response.data.id);
      form.setFieldsValue({ client: response.data.name });
      
      // Close the modal and reset the input
      setIsAddingClient(false);
      setNewClient({
        client_name: '',
        contact_first_name: '',
        contact_last_name: '',
        contact_phone: '',
        contact_email: ''
      });
      
      message.success('Client created successfully');
    } catch (error) {
      console.error('Error creating client:', error);
      message.error('Failed to create client');
    }
  };

  const handleClientChange = (value, option) => {
    setSelectedClient(value);
    if (option) {
      form.setFieldsValue({ client: option.children });
    }
  };

  const handleConvertToSite = async (estimate) => {
    try {
      const response = await api.post(`/estimate/estimates/${estimate.id}/convert_to_site/`);
      message.success('Estimate converted to site successfully');
      // Optionally refresh sites list
      fetchSites();
    } catch (error) {
      console.error('Error converting estimate to site:', error);
      message.error('Failed to convert estimate to site');
    }
  };

  return (
    <ConfigProvider theme={{ token: { colorLinkHover: "#034832", colorPrimary: "#034832" } }}>
      <div className={styles.pageContainer}>
        <div className={styles.leftSection}>
          <div className={styles.estimatesList}>
            <div className={styles.estimatesHeader}>
              <h2 className={styles.headerTitle}>Estimates</h2>
            </div>
            <Button 
              type="primary" 
              onClick={handleCreateEstimate} 
              className={styles.newEstimateBtn}
              icon={<IconPlus size={16} />}
            >
              New Estimate
            </Button>
            <div className={styles.estimatesContainer}>
              {estimates.map((estimate) => (
                <AntdTooltip key={estimate.id} title="View Estimate" placement="right">
                  <div 
                    onClick={() => loadEstimateWithLayers(estimate)}
                    className={`${styles.estimateItem} ${selectedEstimate?.id === estimate.id ? styles.selected : ''}`}
                  >
                    <div className={styles.estimateTitle}>
                      <IconBuildingFactory2 size={16} />
                      {estimate.address}
                    </div>
                    <div className={styles.estimateDetail}>
                      <IconCalendar size={14} />
                      {estimate.date}
                    </div>
                    <div className={styles.estimatePrice}>
                      <span>Total Price:</span>
                      <span style={{ fontWeight: 'bold' }}>
                        ${parseFloat(estimate.total_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </AntdTooltip>
              ))}
            </div>
          </div>

          {isCreatingEstimate ? (
            <div className={`${styles.detailsSection} ${styles.formContainer}`}>
              <h3 className={styles.formHeader}>Enter Estimate Information</h3>
              <div className={styles.formContent}>
                <Form layout="vertical" form={form} requiredMark={false}>
                  <Form.Item label="Address" name="address">
                    <StandaloneSearchBox onLoad={onSearchBoxLoaded} onPlacesChanged={onPlacesChanged}>
                      <Input
                        placeholder="Search For The Address"
                        onChange={(e) => setAddressInput(e.target.value)}
                        prefix={<IconMap size={16} />}
                      />
                    </StandaloneSearchBox>
                  </Form.Item>
                  
                  <Form.Item label="Client" name="client">
                    <Select
                      placeholder="Select a client"
                      value={selectedClient}
                      onChange={handleClientChange}
                      dropdownRender={menu => (
                        <>
                          {menu}
                          <Divider />
                          <Button
                            type="link"
                            style={{ padding: '8px', color: '#034832', display: 'flex', alignItems: 'center' }}
                            onClick={() => setIsAddingClient(true)}
                            icon={<IconPlus size={16} style={{ marginRight: '4px' }} />}
                          >
                            Add New Client
                          </Button>
                        </>
                      )}
                    >
                      {clients.map(client => (
                        <Option key={client.id} value={client.id}>{client.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  
                  <Form.Item label="Date" name="date">
                    <DatePicker onChange={handleDateChange} />
                  </Form.Item>
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Parking Lot Sqft" name="lots_sqft">
                        <Input 
                          value={parkingLotSqft}
                          onChange={(e) => {
                            const value = e.target.value;
                            setParkingLotSqft(value);
                            form.setFieldsValue({ lots_sqft: value });
                          }}
                          prefix={<IconRoad size={16} />}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Walkway Sqft" name="walkway_sqft">
                        <Input 
                          value={walkwaySqft}
                          onChange={(e) => {
                            const value = e.target.value;
                            setWalkwaySqft(value);
                            form.setFieldsValue({ walkway_sqft: value });
                          }}
                          prefix={<IconRuler size={16} />}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={16} className={styles.formRow}>
                     <Col span={12}>
                        <Button 
                          block
                          icon={<IconUpload size={16} />}
                          onClick={() => setUploadModalVisible(true)}
                          className={styles.formButtonRow}
                        >
                          KMZ Upload
                        </Button>
                      </Col>
                      <Col span={12}>
                        <Button 
                          block
                          onClick={() => setShowLayerSelector(true)}
                          disabled={!boundaryPolygon || !boundaryPolygon.features || boundaryPolygon.features.length === 0}
                          className={styles.formButtonRow}
                        >
                          Edit Layers
                        </Button>
                      </Col>
                  </Row>
                  
                  <Form.Item label="Service Level" name="service_level" style={{ marginBottom: '30px' }}>
                    <Slider
                      value={serviceLevel}
                      onChange={(value) => setServiceLevel(Number(value))}
                      valueLabelDisplay="auto" step={1} marks={serviceLevels} min={1} max={5}
                    />
                  </Form.Item>
                  <Form.Item label="Salt Price" name="salt_price" style={{ marginBottom: '30px' }}>
                    <Slider
                      value={saltPrice}
                      onChange={(value) => setSaltPrice(Number(value))}
                      valueLabelDisplay="auto" step={1} marks={saltPriceLevels} min={0} max={200}
                    />
                  </Form.Item>
                </Form>
              </div>
              <div className={styles.formActions}>
                <Button 
                  onClick={handleGenerateEstimate} 
                  type="primary"
                  icon={<IconSnowflake size={16} />}
                  className={styles.actionButton}
                >
                  Generate Estimate
                </Button>
              </div>
            </div>
          ) : selectedEstimate ? (
            <div className={styles.detailsSection}>
              <div className={styles.detailsHeader}>
                <div className={styles.detailsTitle}>
                  <IconBuildingFactory2 size={18} />
                  {selectedEstimate.address}
                </div>
                <IconX 
                  size={18} 
                  onClick={() => {setSelectedEstimate(null); setBoundaryPolygon(null);}} 
                  className={styles.closeIcon}
                />
              </div>
              
              <div className={styles.detailsContent}>
                <div className={styles.detailSection}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Address:</span>
                    <span className={styles.detailValue}>{selectedEstimate.address}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Date:</span>
                    <span className={styles.detailValue}>{selectedEstimate.date}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Client:</span>
                    <span className={styles.detailValue}>{selectedEstimate.client_name ? selectedEstimate.client_name : "N/A"}</span>
                  </div>
                </div>
                
                <div className={styles.detailSection}>
                  <h4 className={styles.sectionTitle}>Property Details</h4>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      <IconRoad size={16} />
                      Parking Lot:
                    </span>
                    <span className={styles.detailValue}>{selectedEstimate.lots_sqft.toLocaleString()} sq. ft</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      <IconRuler size={16} />
                      Walkway:
                    </span>
                    <span className={styles.detailValue}>{selectedEstimate.walkway_sqft.toLocaleString()} sq. ft</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      <IconSnowflake size={16} />
                      Service Level:
                    </span>
                    <span className={styles.detailValue}>{selectedEstimate.service_level}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      <IconSalt size={16} />
                      Salt Level:
                    </span>
                    <span className={styles.detailValue}>${parseFloat(selectedEstimate.salt_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
  
                <div className={styles.detailSection}>
                  <h4 className={styles.sectionTitle}>Pricing Breakdown</h4>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Plowing Price:</span>
                    <span className={styles.detailValue}>${parseFloat(selectedEstimate.plowing_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Salting Price:</span>
                    <span className={styles.detailValue}>${parseFloat(selectedEstimate.salting_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Walkway Price:</span>
                    <span className={styles.detailValue}>${parseFloat(selectedEstimate.walkway_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
  
                <div className={styles.totalPrice}>
                  <span>Total Price:</span>
                  <span>${parseFloat(selectedEstimate.total_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div className={styles.buttonContainer}>
                <Button
                  type="primary"
                  onClick={() => handleEditEstimate(selectedEstimate)}
                  className={styles.actionButton}
                  icon={<IconEdit size={16} />}
                >
                  Edit Estimate
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={() => handleDeleteEstimate(selectedEstimate)}
                  className={styles.actionButton}
                  icon={<IconTrash size={16} />}
                >
                  Delete Estimate
                </Button>
                <Button 
                  type="primary" 
                  onClick={() => handleConvertToSite(selectedEstimate)}
                  className={styles.convertButton}
                >
                  Convert to Site
                </Button>
              </div>
            </div>
          ) : (
             <div className={styles.detailsSection}>
               <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#adb5bd' }}>
                 Select an estimate or create a new one.
               </div>
            </div>
          )}
        </div>

        <div className={styles.rightSection}>
          <MapContainer center={mapPosition || [43.7, -79.4]} zoom={10} className={styles.mapContainer}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {estimates.map((estimate) => (
              <React.Fragment key={estimate.id}>
                <Marker
                  position={[estimate.latitude, estimate.longitude]}
                  icon={customBlueMarkerIcon}
                >
                  <Tooltip>
                    <div>
                      <h4>{estimate.address}</h4>
                      <p>Date: {estimate.date}</p>
                      <h4>Total Price: ${parseFloat(estimate.total_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                    </div>
                  </Tooltip>
                </Marker>
                {selectedEstimate && selectedEstimate.id === estimate.id && estimate.boundary_polygon && 
                  estimate.boundary_polygon.features && 
                  estimate.boundary_polygon.features.map((feature, index) => (
                    feature.geometry && 
                    feature.geometry.type === 'Polygon' && 
                    (feature.properties.is_selected !== false) &&
                    (
                      <Polygon
                        key={`polygon-${estimate.id}-${index}`}
                        positions={feature.geometry.coordinates[0].map(coord => [coord[1], coord[0]])}
                        pathOptions={{ 
                          color: feature.properties.is_walkway ? 'green' : 'red', 
                          fillColor: feature.properties.is_walkway ? 'green' : 'red', 
                          fillOpacity: 0.2,
                          weight: 3
                        }}
                      >
                        <Tooltip>
                          {feature.properties.name || (feature.properties.is_walkway ? 'Walkway' : 'Property Boundary')}
                          {feature.properties.area_sqft && (
                            <div>Area: {Math.round(feature.properties.area_sqft).toLocaleString()} sq ft</div>
                          )}
                          <div>
                            {feature.properties.is_walkway ? 'Walkway' : 'Parking Lot'}
                          </div>
                        </Tooltip>
                      </Polygon>
                    )
                  ))
                }
              </React.Fragment>
            ))}
            {
              newEstimatePosition && (
                <Marker position={newEstimatePosition} icon={customRedMarkerIcon}></Marker>
              )
            }
            {boundaryPolygon && boundaryPolygon.features && !selectedEstimate &&
              boundaryPolygon.features.map((feature, index) => {
                const isParkingLayer = selectedParkingLayers.includes(index);
                const isWalkwayLayer = selectedWalkwayLayers.includes(index);
                const isSelected = isParkingLayer || isWalkwayLayer;
                
                return (feature.geometry && feature.geometry.type === 'Polygon' && (
                  <Polygon
                    key={`new-polygon-${index}`}
                    positions={feature.geometry.coordinates[0].map(coord => [coord[1], coord[0]])}
                    pathOptions={{ 
                      color: isWalkwayLayer ? 'green' : 'red', 
                      fillColor: isWalkwayLayer ? 'green' : 'red', 
                      fillOpacity: isSelected ? 0.2 : 0.05,
                      weight: isSelected ? 3 : 1,
                      dashArray: isSelected ? undefined : '5, 5',
                      opacity: isSelected ? 1 : 0.5
                    }}
                  >
                    <Tooltip>
                      {feature.properties.name || (isWalkwayLayer ? 'Walkway' : 'Property Boundary')}
                      {feature.properties.area_sqft && (
                        <div>Area: {Math.round(feature.properties.area_sqft).toLocaleString()} sq ft</div>
                      )}
                      <div>
                        {isSelected ? 'Selected' : 'Not selected'} - {isWalkwayLayer ? 'Walkway' : isParkingLayer ? 'Parking' : 'Unassigned'}
                      </div>
                    </Tooltip>
                  </Polygon>
                ));
              })
            }
            <SetViewOnClick coords={mapPosition} />
          </MapContainer>
        </div>
      </div>

      <Modal
        title="Edit Estimate"
        open={isEditingEstimate}
        onCancel={() => setIsEditingEstimate(false)}
        centered
        width={600}
        footer={[ <Button onClick={() => setIsEditingEstimate(false)} type='default' style={{boxShadow: 'none'}}>Cancel</Button>, <Button onClick={handleUpdateEstimate} type='primary' style={{boxShadow: 'none'}}>Update Estimate</Button>]}
      >
        <div style={{display:"flex", flexDirection:"column"}}>
          <Form layout="vertical" form={form}>
            <Form.Item label="Address" name="address" style={{ margin: '0', marginBottom: '5px' }}>
              <Input disabled />
            </Form.Item>
            <Form.Item label="Client" name="client" style={{ margin: '0', marginBottom: '5px' }}>
              <Input disabled />
            </Form.Item>
            <Form.Item label="Date" name="date" style={{ margin: '0', marginBottom: '5px' }}>
              <Input disabled />
            </Form.Item>
            <Form.Item label="Parking Lot Sqft" name="lots_sqft" style={{ margin: '0', marginBottom: '5px' }}>
              <Input />
            </Form.Item>
            <Form.Item label="Walkway Sqft" name="walkway_sqft" style={{ margin: '0', marginBottom: '5px' }}>
              <Input />
            </Form.Item>
            <Form.Item label="Service Level" name="service_level" style={{margin: '0', marginBottom: '5px'}}>
              <Slider
                value={serviceLevel}
                onChange={(value) => setServiceLevel(Number(value))}
                aria-labelledby="discrete-slider"
                valueLabelDisplay="auto"
                step={1}
                marks={serviceLevels}
                min={1}
                max={5}
              />
            </Form.Item>
            <Form.Item label="Salt Price" name="salt_price" style={{margin: '0', marginBottom: '5px'}}>
              <Slider
                value={saltPrice}
                onChange={(value) => setSaltPrice(Number(value))}
                valueLabelDisplay="auto"
                step={1}
                marks={saltPriceLevels}
                min={0}
                max={200}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <Modal
        title="Upload KMZ File"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        centered
      >
        <Upload.Dragger
          name="file"
          customRequest={handleKmzUpload}
          showUploadList={false}
          accept=".kmz"
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <IconUpload size={40} stroke={1.5} />
          </p>
          <p className="ant-upload-text">Click or drag KMZ file to this area to upload</p>
          <p className="ant-upload-hint">
            Support for KMZ files containing property boundary data
          </p>
        </Upload.Dragger>
      </Modal>

      <Modal
        title="Select Layers for Area Calculation"
        open={showLayerSelector}
        onCancel={() => setShowLayerSelector(false)}
        width={700}
        footer={[
          <Button key="close" onClick={() => setShowLayerSelector(false)}>
            Done
          </Button>,
          <Button 
            type="primary" 
            onClick={() => {
              handleLayerSelectionChange();
              setShowLayerSelector(false);
              message.success(`Areas updated: ${parkingLotSqft.toLocaleString()} sq.ft parking, ${walkwaySqft.toLocaleString()} sq.ft walkway`);
            }}
            style={{ backgroundColor: '#034832', borderColor: '#034832' }}
          >
            Apply Selection
          </Button>
        ]}
      >
        <div style={{ marginBottom: '20px' }}>
          <p>Select which layers should be included in each area calculation:</p>
        </div>
        
        <div style={{ display: 'flex', gap: '20px' }}>
          {/* Parking Lot Area Column */}
          <div style={{ flex: 1, border: '1px solid #e8e8e8', borderRadius: '4px', padding: '10px' }}>
            <h3 style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
              Parking Lot Area
              <span style={{ float: 'right', fontWeight: 'normal', fontSize: '14px' }}>
                {Math.round(selectedParkingLayers.reduce((total, id) => {
                  const feature = boundaryPolygon?.features?.[id];
                  return total + (feature?.properties?.area_sqft || 0);
                }, 0)).toLocaleString()} sq.ft
              </span>
            </h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '10px' }}>
              {kmzLayers.map((layer) => (
                <div key={`parking-${layer.id}`} style={{ 
                  padding: '8px', 
                  marginBottom: '5px', 
                  backgroundColor: selectedParkingLayers.includes(layer.id) ? '#e6f7e9' : 'transparent',
                  border: `1px solid ${selectedParkingLayers.includes(layer.id) ? '#034832' : '#d9d9d9'}`,
                  borderRadius: '2px',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (selectedParkingLayers.includes(layer.id)) {
                    setSelectedParkingLayers(selectedParkingLayers.filter(id => id !== layer.id));
                  } else {
                    // Add to parking and remove from walkway if it was there
                    setSelectedParkingLayers([...selectedParkingLayers, layer.id]);
                    setSelectedWalkwayLayers(selectedWalkwayLayers.filter(id => id !== layer.id));
                  }
                }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <input 
                        type="checkbox" 
                        checked={selectedParkingLayers.includes(layer.id)} 
                        onChange={e => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            setSelectedParkingLayers([...selectedParkingLayers, layer.id]);
                            setSelectedWalkwayLayers(selectedWalkwayLayers.filter(id => id !== layer.id));
                          } else {
                            setSelectedParkingLayers(selectedParkingLayers.filter(id => id !== layer.id));
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      {layer.name}
                    </div>
                    <div>{Math.round(layer.area_sqft).toLocaleString()} sq.ft</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Walkway Area Column */}
          <div style={{ flex: 1, border: '1px solid #e8e8e8', borderRadius: '4px', padding: '10px' }}>
            <h3 style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
              Walkway Area
              <span style={{ float: 'right', fontWeight: 'normal', fontSize: '14px' }}>
                {Math.round(selectedWalkwayLayers.reduce((total, id) => {
                  const feature = boundaryPolygon?.features?.[id];
                  return total + (feature?.properties?.area_sqft || 0);
                }, 0)).toLocaleString()} sq.ft
              </span>
            </h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '10px' }}>
              {kmzLayers.map((layer) => (
                <div key={`walkway-${layer.id}`} style={{ 
                  padding: '8px', 
                  marginBottom: '5px', 
                  backgroundColor: selectedWalkwayLayers.includes(layer.id) ? '#e6f7e9' : 'transparent',
                  border: `1px solid ${selectedWalkwayLayers.includes(layer.id) ? '#034832' : '#d9d9d9'}`,
                  borderRadius: '2px',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (selectedWalkwayLayers.includes(layer.id)) {
                    setSelectedWalkwayLayers(selectedWalkwayLayers.filter(id => id !== layer.id));
                  } else {
                    // Add to walkway and remove from parking if it was there
                    setSelectedWalkwayLayers([...selectedWalkwayLayers, layer.id]);
                    setSelectedParkingLayers(selectedParkingLayers.filter(id => id !== layer.id));
                  }
                }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <input 
                        type="checkbox" 
                        checked={selectedWalkwayLayers.includes(layer.id)} 
                        onChange={e => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            setSelectedWalkwayLayers([...selectedWalkwayLayers, layer.id]);
                            setSelectedParkingLayers(selectedParkingLayers.filter(id => id !== layer.id));
                          } else {
                            setSelectedWalkwayLayers(selectedWalkwayLayers.filter(id => id !== layer.id));
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      {layer.name}
                    </div>
                    <div>{Math.round(layer.area_sqft).toLocaleString()} sq.ft</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="Add New Client"
        open={isAddingClient}
        onOk={handleCreateClient}
        onCancel={() => {
          setIsAddingClient(false);
          setNewClient({
            client_name: '',
            contact_first_name: '',
            contact_last_name: '',
            contact_phone: '',
            contact_email: ''
          });
        }}
        width={500}
      >
        <Form layout="vertical">
          <Form.Item 
            label="Client Name" 
            required
            rules={[{ required: true, message: 'Please enter client name' }]}
          >
            <Input
              placeholder="Enter client name"
              value={newClient.client_name}
              onChange={e => setNewClient({...newClient, client_name: e.target.value})}
            />
          </Form.Item>
          <Form.Item label="Contact First Name">
            <Input
              placeholder="Enter contact first name"
              value={newClient.contact_first_name}
              onChange={e => setNewClient({...newClient, contact_first_name: e.target.value})}
            />
          </Form.Item>
          <Form.Item label="Contact Last Name">
            <Input
              placeholder="Enter contact last name"
              value={newClient.contact_last_name}
              onChange={e => setNewClient({...newClient, contact_last_name: e.target.value})}
            />
          </Form.Item>
          <Form.Item label="Contact Phone">
            <Input
              placeholder="Enter contact phone"
              value={newClient.contact_phone}
              onChange={e => setNewClient({...newClient, contact_phone: e.target.value})}
            />
          </Form.Item>
          <Form.Item label="Contact Email">
            <Input
              placeholder="Enter contact email"
              value={newClient.contact_email}
              onChange={e => setNewClient({...newClient, contact_email: e.target.value})}
            />
          </Form.Item>
        </Form>
      </Modal>
    </ConfigProvider>
  );
};

export default EstimatePage;